/*
  Custom header search (Childbirth Graphics) — <cbg-header-search>.

  The component wraps the slide-down search strip (rendered as a direct child
  of .header-wrapper in header.liquid). The toggle button lives in the
  mega-menu row ([data-cbg-search-toggle]) and is bound via a document-level
  click delegate — same pattern as cbg-mobile-nav, because toggle and panel
  are in different DOM subtrees.

  Live results come from Shopify's Predictive Search Ajax API, restricted to
  product TITLES, then filtered again client-side so only titles containing
  the exact typed substring are shown (the API alone does per-word prefix
  matching, which is broader than the design allows).

  The same component also powers the MOBILE inline bar (part: 'mobile' in the
  snippet): data-mode="inline" means the bar is always visible — no toggle,
  no open/close of the bar itself, only the results fly-out — and
  data-images="none" renders title-only result rows.
*/

if (!customElements.get('cbg-header-search')) {
  customElements.define(
    'cbg-header-search',
    class CbgHeaderSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-cbg-search-input]');
        this.resetButton = this.querySelector('[data-cbg-search-reset]');
        this.form = this.querySelector('form');
        this.flyout = this.querySelector('[data-cbg-search-flyout]');
        this.resultsList = this.querySelector('[data-cbg-search-results]');
        this.allResultsRow = this.querySelector('[data-cbg-search-all]');

        this.searchUrl = this.dataset.searchUrl || '/search/suggest';
        this.noResultsText = this.dataset.noResultsText || 'No Results';
        this.inline = this.dataset.mode === 'inline';
        this.showImages = this.dataset.images !== 'none';

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onReset = this.onReset.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        this.input.addEventListener('input', this.onInput);
        this.resetButton.addEventListener('click', this.onReset);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        this.input.removeEventListener('input', this.onInput);
        this.resetButton.removeEventListener('click', this.onReset);
        this.form.removeEventListener('submit', this.onSubmit);
        clearTimeout(this.debounceTimer);
        if (this.abortController) this.abortController.abort();
      }

      get isOpen() {
        return this.inline || this.classList.contains('cbg-search--open');
      }

      get toggles() {
        return document.querySelectorAll('[data-cbg-search-toggle]');
      }

      /* ------------------------------------------------------------------
         Open / close
         ------------------------------------------------------------------ */
      open() {
        this.classList.add('cbg-search--open');
        this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
        this.input.focus({ preventScroll: true });
      }

      close() {
        this.closeFlyout();
        this.classList.remove('cbg-search--open');
        this.toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      }

      openFlyout() {
        this.flyout.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      closeFlyout() {
        this.flyout.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
      }

      onDocumentClick(event) {
        const toggle = event.target.closest('[data-cbg-search-toggle]');
        if (toggle) {
          // The toggle belongs to the desktop slide-down bar only; the inline
          // (mobile) instance must not react to it.
          if (!this.inline) this.isOpen ? this.close() : this.open();
          return;
        }
        // Clicking outside the bar closes the fly-out only; the bar itself
        // stays open until the toggle is clicked again (no overlay to catch clicks).
        if (this.isOpen && !this.flyout.hidden && !this.contains(event.target)) {
          this.closeFlyout();
        }
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;
        if (!this.flyout.hidden) {
          this.closeFlyout();
          this.input.focus({ preventScroll: true });
        } else if (!this.inline) {
          this.close(); // the inline bar has no closed state
        }
      }

      onSubmit(event) {
        if (this.input.value.trim() === '') {
          event.preventDefault();
          this.input.focus({ preventScroll: true });
        }
      }

      onReset() {
        clearTimeout(this.debounceTimer);
        if (this.abortController) this.abortController.abort();
        this.input.value = '';
        this.resetButton.hidden = true;
        this.resultsList.innerHTML = '';
        this.closeFlyout();
        this.input.focus({ preventScroll: true });
      }

      /* ------------------------------------------------------------------
         Live search
         ------------------------------------------------------------------ */
      onInput() {
        const query = this.input.value.trim();
        this.resetButton.hidden = query === '';
        clearTimeout(this.debounceTimer);

        if (query === '') {
          if (this.abortController) this.abortController.abort();
          this.resultsList.innerHTML = '';
          this.closeFlyout();
          return;
        }

        this.debounceTimer = setTimeout(() => this.search(query), 250);
      }

      search(query) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title',
        });

        fetch(`${this.searchUrl}.json?${params}`, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
            return response.json();
          })
          .then((data) => {
            const products = (((data.resources || {}).results || {}).products || []).filter((product) =>
              product.title.toLowerCase().includes(query.toLowerCase())
            );
            this.renderResults(products, query);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            this.renderResults([], query); // degrade to the "No Results" state
          });
      }

      renderResults(products, query) {
        this.resultsList.innerHTML = '';

        if (products.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'cbg-search__empty';
          empty.textContent = this.noResultsText;
          this.resultsList.appendChild(empty);
          this.allResultsRow.hidden = true;
        } else {
          products.forEach((product) => this.resultsList.appendChild(this.buildItem(product, query)));
          this.allResultsRow.hidden = false;
        }

        this.openFlyout();
      }

      buildItem(product, query) {
        const item = document.createElement('a');
        item.className = 'cbg-search__item';
        item.href = product.url;
        item.setAttribute('role', 'option');

        const imageUrl =
          (product.featured_image && product.featured_image.url) || product.image || '';
        if (this.showImages && imageUrl) {
          const image = document.createElement('img');
          image.className = 'cbg-search__item-img';
          image.src = this.sizedImageUrl(imageUrl);
          image.alt = (product.featured_image && product.featured_image.alt) || '';
          image.loading = 'lazy';
          image.setAttribute('width', '100');
          image.setAttribute('height', '');
          item.appendChild(image);
        }

        const content = document.createElement('div');
        content.className = 'cbg-search__item-content';
        const title = document.createElement('div');
        title.className = 'cbg-search__item-title';
        title.innerHTML = this.highlight(product.title, query);
        content.appendChild(title);
        item.appendChild(content);

        return item;
      }

      // Requests a reasonably sized rendition from the Shopify CDN.
      sizedImageUrl(url) {
        try {
          const parsed = new URL(url, window.location.origin);
          parsed.searchParams.set('width', '200');
          return parsed.toString();
        } catch (error) {
          return url;
        }
      }

      // Escapes the title and wraps every occurrence of the typed substring
      // in a <mark>. Split keeps matches at the odd indexes.
      highlight(text, query) {
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text
          .split(new RegExp(`(${escapedQuery})`, 'gi'))
          .map((part, index) =>
            index % 2 === 1
              ? `<mark class="cbg-search__mark">${this.escapeHtml(part)}</mark>`
              : this.escapeHtml(part)
          )
          .join('');
      }

      escapeHtml(text) {
        return text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
